import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import DynamicForm from "../../stories/Forms/DynamicForm";
import { secondaryBarcodeFields } from "../../stories/Forms/fields";
import useApis from "../../services/useAPI";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import { Spinner } from "react-bootstrap";
import { accessError } from "../../utils/constants";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import { useAuth } from "../../components/AuthContext/AuthContext";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";

/**
 * Calculate progress bar steps for secondary barcode page
 */
export const calculateSecondaryBarcodeProgressSteps = (testData = null) => {
  // Steps: Barcode -> Secondary Barcode -> Consent -> Assessment -> Results
  let totalSteps = 4;
  let currentStep = 2; // Currently on step 2 (Secondary Barcode)
  let completedSteps = 1; // Step 1 (Enter Barcode) is completed

  return {
    totalSteps,
    currentStep,
    completedSteps
  };
};

/**
 * SecondaryBarcode component
 *
 * This component renders a form for users to confirm their secondary barcode.
 * It fetches the barcode from the API response and displays it in a read-only field.
 * The user can either confirm the barcode is correct or navigate away if incorrect.
 *
 * @returns {JSX.Element} The rendered form for confirming the secondary barcode.
 */
function SecondaryBarcode() {
  const { primaryBarcode } = useParams();
  const { callApi, resultGet, serverError } = useApis();
  const [isLoading, setIsLoading] = useState(true);
  const barcodeValue = primaryBarcode?.primaryBarcode || primaryBarcode;
  const typeUrl = `/api/dashboard/check-test/${barcodeValue}/`;
  const postApiUrl = `/api/dashboard/activating/`;
  const [error, setError] = useState("");
  const [previousButton, setPreviousButton] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation()

  /**
   * Update progress steps when component mounts or data changes
   */
  useEffect(() => {
    const newProgressSteps = calculateProgressSteps(resultGet, resultGet?.type, location.pathname);
    auth?.setProgressSteps(newProgressSteps);
  }, [resultGet]);

  useEffect(() => {
    if (barcodeValue) {
      setIsLoading(true);
      callApi({}, typeUrl, "GET");
    }
  }, [barcodeValue, typeUrl]);

  useEffect(() => {
    setError(serverError);
  }, [serverError])

  useEffect(() => {
    if (resultGet?.activation_stage === null) {
      setError(accessError);
    }
    if (resultGet?.secondary_barcode_id === null) {
      setPreviousButton(true);
      setError(`Primary Barcode ${primaryBarcode} Not Found`);
    }
    if (resultGet?.activation_stage) {
      // Check if activation_stage is one of the allowed values
      const allowedStages = [
        "consent",
        "personal-details",
        "collect-sample",
        "sh-consent",
        "barcode",
        "confirm-details",
        "secondary-barcode"
      ];

      if (resultGet.activation_stage && !allowedStages.includes(resultGet.activation_stage)) {
        setError(accessError);
      } else if (allowedStages.includes(resultGet.activation_stage)) {
        setIsLoading(false);
      } else {
        setError("");
      }
    }
  }, [resultGet]);

  useEffect(() => {
    if (resultGet) {
      localStorage.setItem("primary_barcode_id", resultGet?.primary_barcode_id);
      localStorage.setItem("secondary_barcode_id",resultGet?.secondary_barcode_id || null,);
    }
  }, [resultGet]);

  if (error) {
    return <ErrorDisplay error={error} consent={previousButton} />;
  }

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border"  className="my-custom-spinner"/>
      </div>
    );
  }

  const handleSubmit = async () => {
    try {
      const payload = {
        activation_stage: "secondary-barcode",
        code: primaryBarcode
      };

      const response = await callApi(payload, postApiUrl, "POST");

      // Check for the specific success message
      if (response && response.message === "Activation Stage Saved!") {
        // Update progress steps before navigation
        const updatedProgressSteps = updateProgressAfterCompletion(resultGet, resultGet?.type, location.pathname)
        auth?.setProgressSteps(updatedProgressSteps);

        navigate(`/confirm-details/${primaryBarcode}`);
      }
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className="row justify-content-center secondary-barcode-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth?.progressSteps?.totalSteps || 5}
          currentStep={auth?.progressSteps?.currentStep || 2}
          completedSteps={auth?.progressSteps?.completedSteps || 1}
          isCompleted={auth?.progressSteps?.completedSteps || 1}
          is_assessment_completed={false}
        />
        <h1 className="my-4">Check your second barcode</h1>
        <p className="text-muted">
          Please check and confirm your{" "}
          {resultGet?.secondary_barcode_type === "lavender" ? "lavender".toUpperCase() : "blue".toUpperCase()} barcode is
          correct.
        </p>
        <DynamicForm
          fields={secondaryBarcodeFields(primaryBarcode, handleSubmit, resultGet?.secondary_barcode_id)}
          className={"barcode-form"}
          btnSecondary={true}
        />
      </div>
    </div>
  );
}

export default SecondaryBarcode;